import { useCallback, useEffect, useState, type SyntheticEvent } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { ElementRenderer } from "./element-renderer"
import { ContextMenu } from "../components/ui/context-menu"
import type { CogoElement } from "../core/types"
import { cn } from "../lib/utils"
import { readResolvedArray, recordValue, resolvedText, shorthandId } from "./property-value-utils"

function shorthandChildren(
  element: RendererProps["element"],
  resolveSymbol: (value: string) => unknown,
  items: unknown[],
): CogoElement[] {
  if (element.children?.length) return element.children
  if (items.length === 0) return []
  return [
    {
      id: `${element.id}__items_trigger`,
      type: "context-menu-trigger",
      text: resolvedText(element.label ?? element.text, resolveSymbol, "Context menu"),
    },
    {
      id: `${element.id}__items_content`,
      type: "context-menu-content",
      children: items.map((raw, index) => {
        const item = recordValue(raw)
        return {
          ...item,
          id: String(item.id ?? shorthandId(element.id, "items", index)),
          type: item.type === "separator" ? "context-menu-separator" : "context-menu-item",
          text: item.label ?? item.text,
          disabled: item.disabled,
        } as unknown as CogoElement
      }),
    },
  ]
}

function resolveHighlightedIndex(
  value: unknown,
  resolveSymbol: (value: string) => unknown,
): number {
  const resolved = typeof value === "string" ? resolveSymbol(value) : value
  const index = Number(resolved)
  return Number.isFinite(index) && index >= 0 ? Math.trunc(index) : -1
}

/**
 * Context-menu items can be nested in content/group nodes. Preserve that
 * authored structure while applying the controlled highlight to the item at
 * its flattened menu index.
 */
function withHighlightedItem(
  children: CogoElement[],
  highlightedIndex: number,
): CogoElement[] {
  let itemIndex = 0

  const visit = (child: CogoElement): CogoElement => {
    const currentIndex = child.type === "context-menu-item" ? itemIndex++ : -1
    const nested = child.children?.map(visit)
    return {
      ...child,
      ...(nested ? { children: nested } : {}),
      ...(currentIndex === highlightedIndex
        ? { className: cn(child.className, "bg-accent text-accent-foreground") }
        : {}),
    }
  }

  return children.map(visit)
}

/**
 * ContextMenuRenderer — base-ui ContextMenu.Root provider.
 *
 * Children should include context-menu-trigger (the right-click target)
 * + context-menu-content (with context-menu-item children).
 *
 * Open state options (mirrors DropdownMenuRenderer):
 *  - Uncontrolled: omit `bind` / `open` / `defaultOpen` → Root manages internally (right-click)
 *  - Bound: element.bind = "#{ui.menuOpen}" → state-controlled via ActionFlow
 *  - defaultOpen: element.defaultOpen = true → uncontrolled force-open (preview / initial-open)
 */
export function ContextMenuRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: ContextMenu root is a base-ui provider that
  // doesn't render a DOM element of its own, so we surface the atom
  // contract on a thin wrapping span. The portal'd trigger/content inside
  // continue to render normally.
  const atomDefaults = useAtomDefaults("context-menu")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const [bound, setBound] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    (element as unknown as { defaultOpen?: boolean }).defaultOpen === true,
    Boolean,
  )
  const authoredHighlightedIndex = resolveHighlightedIndex(
    (element as unknown as { highlightedIndex?: unknown }).highlightedIndex,
    resolveSymbol,
  )
  const [highlightedIndex, setHighlightedIndex] = useState(authoredHighlightedIndex)

  useEffect(() => {
    setHighlightedIndex(authoredHighlightedIndex)
  }, [authoredHighlightedIndex])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (element.bind !== undefined) setBound(open)
      dispatchEvent(element.id, "openChange", { open })
    },
    [element.id, element.bind, setBound, dispatchEvent],
  )

  const rootProps: Record<string, unknown> =
    element.bind !== undefined
      ? { open: bound, onOpenChange: handleOpenChange }
      : (element as unknown as { defaultOpen?: boolean }).defaultOpen !== undefined
        ? { defaultOpen: (element as unknown as { defaultOpen?: boolean }).defaultOpen === true, onOpenChange: handleOpenChange }
        : { onOpenChange: handleOpenChange }
  const items = readResolvedArray(element, "items", resolveSymbol)
  const children = withHighlightedItem(
    shorthandChildren(element, resolveSymbol, items),
    highlightedIndex,
  )

  const handleHighlightCapture = useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const item = target.closest<HTMLElement>('[data-slot="context-menu-item"]')
      const content = item?.closest<HTMLElement>('[data-slot="context-menu-content"]')
      if (!item || !content) return

      const menuItems = Array.from(
        content.querySelectorAll<HTMLElement>('[data-slot="context-menu-item"]'),
      )
      const nextIndex = menuItems.indexOf(item)
      if (nextIndex < 0 || nextIndex === highlightedIndex) return

      setHighlightedIndex(nextIndex)
      dispatchEvent(element.id, "highlightChange", {
        highlightedIndex: nextIndex,
        index: nextIndex,
      })
    },
    [dispatchEvent, element.id, highlightedIndex],
  )

  return (
    <span
      data-atom-type="context-menu"
      data-cogo-logical-host={element.id}
      data-items={JSON.stringify(items)}
      data-highlighted-index={highlightedIndex}
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-testid={`context-menu-${element.id}`}
      className={element.className}
      onPointerMoveCapture={handleHighlightCapture}
      onFocusCapture={handleHighlightCapture}
      style={{ display: "inline-flex", minHeight: 1, minWidth: 1 }}
    >
      <ContextMenu {...rootProps}>
        {children.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </ContextMenu>
    </span>
  )
}
